import { DocumentSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/TransactionDetails/documents/DocumentSection';
import { buildUploadEntityDetails } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/TransactionDetails/utils.ts';
import { AuditView } from '@dealstack/frontend/shared/components/Audit/Audit.tsx';
import {
	AuditContext,
	useAuditController,
} from '@dealstack/frontend/shared/components/Audit/useAuditController.ts';
import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { TransactionWithSecurityTct } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Flex } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { MantineTabProps } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.types';

enum TRANSACTION_TABS {
	OVERVIEW = 'OVERVIEW',
	DOCUMENTS = 'DOCUMENTS',
	HISTORY = 'HISTORY',
}

interface Props {
	transaction?: TransactionWithSecurityTct;
	personSelectOptions: {
		label: string;
		value: string;
	}[];
	overviewContent: JSX.Element;
}

export const TransactionTabs = ({ transaction, personSelectOptions, overviewContent }: Props) => {
	const tCapManage = useTranslationCapManage();
	const auditController = useAuditController(transaction?.id);
	const [activeTab, setActiveTab] = useState<TRANSACTION_TABS>(TRANSACTION_TABS.OVERVIEW);

	//go to default tab when navigate between transactions
	useEffect(() => {
		if (transaction?.id) {
			setActiveTab(TRANSACTION_TABS.OVERVIEW);
		}
	}, [transaction?.id]);

	const tabsConfig = useMemo(
		(): MantineTabProps<TRANSACTION_TABS>[] => [
			{
				label: tCapManage('securities.details.overview'),
				value: TRANSACTION_TABS.OVERVIEW,
			},
			{
				label: tCapManage('documents.documents'),
				value: TRANSACTION_TABS.DOCUMENTS,
				testId: TEST_ELEMENT_IDS.CAP_TRANSACTION_DETAILS_DOCUMENTS_TAB,
			},
			{
				label: tCapManage('transactions.details.history_title'),
				value: TRANSACTION_TABS.HISTORY,
			},
		],
		[tCapManage]
	);

	const panelsConfig = useMemo(() => {
		const uploadEntityDetails =
			transaction && buildUploadEntityDetails(transaction, personSelectOptions);

		return [
			{
				value: TRANSACTION_TABS.OVERVIEW,
				content: overviewContent,
			},
			{
				value: TRANSACTION_TABS.DOCUMENTS,
				content: (
					<Flex direction="column" h="100%">
						{transaction && (
							<DocumentSection
								transactionId={transaction.id}
								uploadEntityDetails={
									uploadEntityDetails ? [uploadEntityDetails] : undefined
								}
							/>
						)}
					</Flex>
				),
			},
			{
				value: TRANSACTION_TABS.HISTORY,
				content: <AuditView />,
			},
		];
	}, [overviewContent, transaction, personSelectOptions]);

	return (
		<AuditContext.Provider value={auditController}>
			<MantineTabs
				defaultValue={TRANSACTION_TABS.OVERVIEW}
				value={activeTab}
				onTabChange={(tab: TRANSACTION_TABS) => {
					setActiveTab(tab);
				}}
				tabs={tabsConfig}
				panels={panelsConfig}
			/>
		</AuditContext.Provider>
	);
};
